/**
 * @file confirmDialog.jsx
 * @description A promise-based confirm, drawn with the app's Modal.
 *
 *   const ok = await confirmDialog({ question: 'Delete this question?', note: 'Answers already given keep their text.', confirmLabel: 'Delete', tone: 'danger' });
 *
 * Fifty-six screens asked with `window.confirm(...)`: a native box in the
 * browser's font with an OK button, sixteen of them saying nothing about the
 * consequence, two of them carrying a three-way decision on a yes/no control.
 * This replaces the box, not the call shape — a string still works
 * (`confirmDialog('Remove this card?')`), so the migration was mechanical and
 * the copy can be improved site by site.
 *
 * `ConfirmHost` is mounted once in main.jsx. `confirmDialog` resolves true on
 * confirm, false on cancel, Escape or backdrop.
 */
import { useEffect, useRef, useState } from 'react';
import { Modal } from './Modal.jsx';
import { Button } from './Button.jsx';
import { Text } from './text.element.jsx';

let host = null;
const queue = [];

const normalise = (input) => {
  if (input && typeof input === 'object') return input;
  const text = String(input ?? '').trim();
  // "Question?\n\nWhy it matters." — the first paragraph asks, the rest explains.
  const [question, ...rest] = text.split(/\n\s*\n/);
  return { question: question || 'Are you sure?', note: rest.join(' ').replace(/\s*\n\s*/g, ' ') };
};

/**
 * Ask for a REASON, not just a yes.
 *
 * Some acts change a person's standing — suspending an account revokes every
 * live session, a soft delete hides them, a hard delete cannot be undone —
 * and the record for all three carried who and when and never why. The
 * platform already holds this standard where money and accreditation are
 * concerned, in its own words: "A decision on somebody's money needs a stated
 * reason", "Give a reason — the candidate is shown it". Account actions were
 * the gap.
 *
 * Resolves to the trimmed reason, or `null` if cancelled — so a caller can
 * treat "no reason" and "changed my mind" as the same non-event.
 *
 * @returns {Promise<string|null>}
 */
export function reasonDialog(input) {
  const opts = { ...normalise(input), needsReason: true };
  return new Promise((resolve) => {
    queue.push({ opts, resolve });
    if (host) host();
    else if (typeof window !== 'undefined') {
      // No host mounted (a test, or a very early call). Fall back to the
      // browser's own prompt rather than resolving as though somebody typed
      // something.
      const text = window.prompt([opts.question, opts.note].filter(Boolean).join('\n\n'));
      queue.pop();
      const trimmed = String(text || '').trim();
      resolve(trimmed.length >= (opts.minLength ?? 8) ? trimmed : null);
    }
  });
}

/** @returns {Promise<boolean>} */
export function confirmDialog(input) {
  const opts = normalise(input);
  return new Promise((resolve) => {
    queue.push({ opts, resolve });
    if (host) host();
    else if (typeof window !== 'undefined' && !host) {
      // No host mounted (a test, or a very early call): fall back to the
      // browser's box rather than silently confirming.
      const ok = window.confirm([opts.question, opts.note].filter(Boolean).join('\n\n'));
      queue.pop();
      resolve(ok);
    }
  });
}

export function ConfirmHost() {
  const [current, setCurrent] = useState(null);
  const [reason, setReason] = useState('');
  const confirmRef = useRef(null);

  useEffect(() => {
    host = () => setCurrent((c) => c || queue.shift() || null);
    host();
    // Dev-only handle so the dialog can be exercised from the console.
    if (import.meta.env?.DEV && typeof window !== 'undefined') window.__moksyConfirm = confirmDialog;
    return () => { host = null; };
  }, []);

  const finish = (ok) => {
    // A reason dialog resolves the TEXT (or null); a confirm resolves a
    // boolean. Same host, two contracts, decided by which asked.
    current?.resolve(current?.opts?.needsReason ? (ok ? reason.trim() : null) : ok);
    setReason('');
    setCurrent(queue.shift() || null);
  };

  if (!current) return null;
  const {
    question, note, confirmLabel = 'Confirm', cancelLabel = 'Cancel',
    tone = 'default', title, needsReason = false, minLength = 8,
    reasonLabel = 'Why', reasonHint = 'Recorded against the account, and shown to whoever asks next.', reasonPlaceholder = ''
  } = current.opts;
  const reasonTooShort = needsReason && reason.trim().length < minLength;
  const danger = tone === 'danger' || /\b(delete|remove|erase|revoke|cancel|refund|clawback|shut)/i.test(`${confirmLabel} ${question}`);

  return (
    <Modal
      open
      onClose={() => finish(false)}
      title={title || question}
      size="sm"
      initialFocusRef={confirmRef}
      footer={(
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={() => finish(false)}>{cancelLabel}</Button>
          <Button ref={confirmRef} variant={danger ? 'danger' : 'primary'}
                  disabled={reasonTooShort}
                  onClick={() => finish(true)}>{confirmLabel}</Button>
        </div>
      )}
    >
      {note ? <Text variant="paragraph" className="text-gray-600">{note}</Text> : null}
      {needsReason ? (
        <div className="mt-3">
          <label className="block text-xs font-medium text-neutral" htmlFor="confirm-reason">{reasonLabel}</label>
          <textarea
            id="confirm-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            placeholder={reasonPlaceholder}
          />
          <p className="mt-1 text-[11px] text-gray-500">{reasonHint}</p>
        </div>
      ) : null}
    </Modal>
  );
}

export default confirmDialog;
